import { EditorHistory, duplicateLayer } from './editor_history.js';

export const HistoryMethods = {
  _initializeHistory({ reset = false } = {}) {
    if (!this.workspaceValue || !this._stage) return;
    const snapshot = JSON.stringify(this._toJson());
    if (reset || !this._history || this._historyImage !== this.reinitializeValue) {
      this._history = new EditorHistory(snapshot);
      this._historyImage = this.reinitializeValue;
      this._mapEdits = {};
      this._trackMapEdit('canvas', snapshot, snapshot);
      const position = this._positionSnapshot();
      this._trackMapEdit('position', position, position);
    } else this._history.current = snapshot;
    this._historyRevision = this.canvasRevisionValue;
    this._syncHistoryControls();
  },

  _recordHistory(snapshot) {
    if (!this.workspaceValue || this._applyingHistory) return;
    this._history?.record(snapshot);
    this._syncHistoryControls();
  },

  _syncHistoryControls() {
    for (const button of this.undoTargets || [])
      button.disabled = !this._history?.past.length || !!this._canvasConflict;
    for (const button of this.redoTargets || [])
      button.disabled = !this._history?.future.length || !!this._canvasConflict;
  },

  undoMapEdit(event) {
    event?.preventDefault();
    this._applyHistory('undo');
  },

  redoMapEdit(event) {
    event?.preventDefault();
    this._applyHistory('redo');
  },

  _applyHistory(direction) {
    if (!this.workspaceValue || !this._stage || this._canvasConflict) return;
    this._cancelCanvasGesture();
    this._finishShapeAnimations?.();
    this._clearCharacterMove?.();
    const snapshot = this._history?.[direction]();
    if (!snapshot) return;
    this._applyingHistory = true;
    try {
      this._restoreFromJson(JSON.parse(snapshot));
      this._save();
    } finally {
      this._applyingHistory = false;
      this._syncHistoryControls();
    }
  },

  duplicateSelectedLayer(event) {
    event?.preventDefault();
    const shape = this._findShapeById(this._selectedShapeId);
    if (!this.workspaceValue || !this._canEditShape(shape)) return;
    this._finishShapeAnimations?.();
    const result = duplicateLayer(this._toJson(), shape.id, this.gridSizeValue || 50);
    this._restoreFromJson(result.scene);
    this._selectShape(result.id);
    this._save();
  },

  toggleAutosave(event) {
    this._autosaveEnabled = event.currentTarget.checked;
    try {
      localStorage.setItem('konva-map:autosave', String(this._autosaveEnabled));
    } catch {
      // Storage may be unavailable; keep the preference for this editor session.
    }
    if (this._autosaveEnabled) this.saveMap();
    else {
      clearTimeout(this._canvasSaveDebounce);
      clearTimeout(this._positionSaveDebounce);
      if (this._pendingCanvasJson) this._setSaveState('canvas', 'dirty');
      if (this._pendingPositionJson) this._setSaveState('position', 'dirty');
    }
    this._syncSaveStatus();
  },

  _trackMapEdit(kind, snapshot, baseline) {
    this._mapEdits ||= {};
    const edits = (this._mapEdits[kind] ||= { snapshot: baseline, saved: baseline, version: 0, savedVersion: 0 });
    if (edits.snapshot !== snapshot) {
      edits.snapshot = snapshot;
      edits.version++;
    }
  },

  _acknowledgeMapEdits(kind, snapshot, version) {
    const edits = this._mapEdits?.[kind];
    if (edits) Object.assign(edits, { saved: snapshot, savedVersion: version });
  },

  _clearSavedMapDraft(kind) {
    const suffix = kind === 'canvas' ? 'CanvasJson' : 'PositionJson';
    const pending = this[`_pending${suffix}`];
    // A running or failed request may still have changed the server. Keep the
    // latest draft until that write is acknowledged or explicitly retried.
    if (!pending || this[`_saving${suffix}`] || this[`_failed${suffix}`]) return false;
    const edits = this._mapEdits?.[kind];
    if (pending !== edits?.saved) return false;
    this[`_pending${suffix}`] = null;
    this._acknowledgeMapEdits(kind, pending, edits.version);
    if (kind === 'canvas') this._canvasBaseRevision = null;
    return true;
  },

  _unsavedMapEditCount() {
    return Object.values(this._mapEdits || {}).reduce(
      (count, edits) => count + (edits.snapshot === edits.saved ? 0 : edits.version - edits.savedVersion),
      0
    );
  },

  saveMap(event) {
    event?.preventDefault();
    if (this._pendingCanvasJson || this._failedCanvasJson) {
      this._pendingCanvasJson ||= this._failedCanvasJson;
      this._flushCanvasSave();
    }
    if (this._pendingPositionJson || this._failedPositionJson) {
      this._pendingPositionJson ||= this._failedPositionJson;
      this._flushPositionSave();
    }
  },

  _hasUnsavedMapChanges() {
    return !!(
      this._pendingCanvasJson ||
      this._savingCanvasJson ||
      this._failedCanvasJson ||
      this._pendingPositionJson ||
      this._savingPositionJson ||
      this._failedPositionJson
    );
  }
};
